import { customAlphabet } from 'nanoid';
import { v7 as uuidv7 } from 'uuid';
import { z } from 'zod';

/**
 * Primary key for any domain table. UUID v7 — sortable by creation time,
 * B-tree friendly in SQLite/D1, RFC 9562 standard.
 */
export const newId = (): string => uuidv7();

/**
 * Custom alphabet without ambiguous characters (no 0/O/I/l/1).
 * Safe for SMS, manual typing and small fonts.
 */
const TOKEN_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz';

/**
 * Public token printed in the SMS link the driver opens to see the timer.
 * 22 chars in our alphabet = ~131 bits of entropy.
 */
export const newPublicTicketToken = customAlphabet(TOKEN_ALPHABET, 22);

/** O código curto impresso no ticket ("Ticket #MQL289"): os 6 últimos do token. */
export const ticketCodeOf = (token: string): string => token.slice(-6).toUpperCase();

/**
 * O código do ticket como alguém digitou (#621): com ou sem "#", em qualquer
 * caixa. `null` quando o texto não tem a forma de um código.
 */
export const parseTicketCode = (input: string): string | null => {
  const code = input.trim().replace(/^#/, '').toUpperCase();
  return /^[2-9A-Z]{6}$/.test(code) ? code : null;
};

/**
 * Single-use invite token used by operators to claim a session.
 * 32 chars in our alphabet = ~190 bits of entropy.
 */
export const newInviteToken = customAlphabet(TOKEN_ALPHABET, 32);

/**
 * Public token for the monthly subscriber payment page (`/p/:token`).
 * One per billing cycle. 22 chars = ~131 bits of entropy.
 */
export const newPaymentLinkToken = customAlphabet(TOKEN_ALPHABET, 22);

/**
 * Public token for the NFS-e page (`/n/:token`) the customer gets by e-mail,
 * SMS or WhatsApp (#527). 22 chars = ~131 bits of entropy.
 */
export const newNfseNoteToken = customAlphabet(TOKEN_ALPHABET, 22);

/**
 * Re-exposed for tests and ad-hoc usage when the caller wants a custom length.
 */
export const TOKEN_ALPHABET_RAW = TOKEN_ALPHABET;

/**
 * Validates a public ticket token: exactly 22 chars from the token alphabet.
 * Used to accept a CLIENT-generated token on ticket open (#262) — the operator
 * app mints the token offline so the receipt/QR work without a round-trip, and
 * this schema stops a client from injecting an arbitrary or malformed string.
 */
export const publicTicketTokenSchema = z
  .string()
  .length(22)
  .regex(new RegExp(`^[${TOKEN_ALPHABET}]+$`), 'invalid token');
